import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import _ from "lodash";
import { format } from "lib/globalize-util";
import classNames from "classnames";
import ReactRotate from "react-rotation";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { InlineLoading } from "components/content-loading";
import { themeOptions } from "styles/theme";
import SpinLeftIcon from "components/icons/spin-left";
import SpinRightIcon from "components/icons/spin-right";
import Image360Icon from "components/icons/image-360";

/**
 * Calculates play speed depending on images number.
 * Rotation time varies linearly from 1 to 5 second due to images number. Used for a smoother view.
 */
const AUTOPLAY = (size: number) => Math.round(_.clamp(size * 50, 1500, 5000) / size);

interface IProps {
    /** Array of images src */
    source: string[];
    height?: string | number;
    width?: string | number;
    /** start rotation after loading of all images */
    hint?: boolean;
    /** show 360 image indicator in left bottom conner */
    indicator?: boolean;
    /** show internal spinner while all images are not load into memory */
    preload?: boolean;
    /** show internal spinner for external loading process for preparing source data */
    preloading?: boolean;
    /** start rotation after loading of all images */
    autoPlay?: boolean;
    /** className of the root element */
    className?: string;
    // react rotate props
    /** Rotate images in infinite loop. Default to true */
    cycle?: boolean;
    /** Rotate images using mouse scroll. Default to false */
    scroll?: boolean;
    isReadOnly?: boolean;
    image360ref?: React.MutableRefObject<HTMLDivElement>;
    ["data-test"]?: string;
}

export const Image360 = (props: IProps) => {
    const { source, width, height, className, image360ref } = props;
    const {
        cycle = true,
        preload = true,
        hint,
        indicator,
        scroll = false,
        autoPlay = false,
        preloading,
        isReadOnly = false,
    } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const [showHint, setShowHint] = useState<boolean>(true);
    const loadStatus = useRef<Set<string>>(new Set());
    const rotateRef = useRef<any>(null);
    const sourceSet = useRef<Set<string>>(new Set(source));

    useSafariFix(rotateRef); // TODO: should be fix inside of image-rotation lib or replaced by own library

    useEffect(() => {
        if (_.some(sourceSet.current, (url: string) => !loadStatus.current.has(url))) {
            setLoading(true);
            setReady(false);
        }
        if (_.every(sourceSet.current, (url: string) => !loadStatus.current.has(url))) {
            loadStatus.current = new Set();
        }
    }, [source]);

    const handleLoad = useCallback(
        (url: string) => () => {
            loadStatus.current.add(url);
            if (
                loadStatus.current.size >= sourceSet.current.size &&
                _.every(sourceSet.current, (url: string) => loadStatus.current.has(url))
            ) {
                setLoading(false);
                setReady(true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [source],
    );

    const handleChange = useCallback(() => setShowHint(false), []);

    const isLoading = preloading || (preload && loading);
    const classes = useStyles({ width, height });
    return (
        <div className={classNames(classes.root, className)} ref={image360ref} data-test={props["data-test"]}>
            {isLoading && <InlineLoading className={classes.loading} />}
            {isReadOnly ? (
                <div className={classNames(classes.rotate, { hidden: isLoading })}>
                    <img className={classes.image} src={source[0]} alt="360" onLoad={handleLoad(source[0])} />
                </div>
            ) : (
                <ReactRotate
                    ref={rotateRef}
                    className={classNames(classes.rotate, props["data-test"] + "rotate", {
                        hidden: !ready || isLoading,
                    })}
                    cycle={cycle}
                    scroll={scroll}
                    autoPlay={autoPlay && ready ? AUTOPLAY(_.size(source)) : false}
                    onChange={handleChange}
                    id={props["data-test"] + "rotate"}
                >
                    {_.map(source, (src, idx) => (
                        <img className={classes.image} key={idx} src={src} alt="360" onLoad={handleLoad(src)} />
                    ))}
                </ReactRotate>
            )}
            {indicator && !isLoading && <Image360Icon className={classes.indicator} />}
            {hint && (
                <div className={classNames(classes.hint, { visible: showHint && !isLoading })}>
                    <SpinLeftIcon className={classes.icon} />
                    {format("Drag to spin")}
                    <SpinRightIcon className={classes.icon} />
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme, { width: string | number; height: string | number }>({
    root: {
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
    },
    rotate: {
        cursor: "ew-resize",
        "&.hidden": {
            display: "none",
        },
    },
    loading: {
        alignItems: "center",
        height: "100%",
        margin: "auto",
    },
    image: ({ width, height }) => ({
        width: width || "100%",
        height: height || "100%",
        objectFit: "contain",
        pointerEvents: "none",
    }),
    hint: {
        display: "flex",
        alignItems: "center",
        position: "absolute",
        bottom: 0,
        padding: 10,
        backgroundColor: "rgba(245,245,245,0.7)",
        borderRadius: "8px 8px 0 0",
        fontSize: 14,
        color: themeOptions.palette.darkGrey,
        opacity: 0,
        transition: "opacity 600ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        "&.visible": {
            opacity: 1,
        },
    },
    indicator: {
        position: "absolute",
        bottom: 10,
        left: 10,
        width: 36,
        height: 36,
        padding: 3,
        backgroundColor: "rgba(245,245,245,0.7)",
        borderRadius: "50%",
    },
    icon: {
        width: 14,
        height: 14,
        color: themeOptions.palette.darkGrey,
        margin: "0 5px",
    },
});

function useSafariFix(rotateRef) {
    // safari can not normally process preventing event defaults for mouseup
    const safariFix = useCallback(() => {
        if (rotateRef.current) {
            rotateRef.current.pointerPosition = null;
            rotateRef.current.startFrame = null;
        }
    }, [rotateRef]);
    useEffect(() => {
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) && rotateRef.current) {
            document.removeEventListener("mouseup", rotateRef.current.touchEnd);
            document.addEventListener("mouseup", safariFix);
        }
        return () => document.removeEventListener("mouseup", safariFix);
    }, [rotateRef.current]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default Image360;
